import { OrderDisputeStatuses } from 'constants/orderDisputeStatuses';
import { Resources } from 'constants/resources';

import React from 'react';

import { FormControl } from '@mui/material';
import { DialogProps } from '@mui/material/Dialog/Dialog';
import { updateDispute } from 'config/requests/orders';
import { queryClient } from 'providers/queryClient';
import { useGetList } from 'react-admin';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'shared/mui/Buttons';
import { Card, CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { DatePicker } from 'shared/mui/DatePicker/DatePicker/DatePicker';
import { Dialog } from 'shared/mui/Dialogs';
import { TextField } from 'shared/mui/Fields';
import { FormHelperText } from 'shared/mui/FormHelperText/FormHelperText';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { InputLabel } from 'shared/mui/Labels/InputLabel/InputLabel';
import { MenuItem } from 'shared/mui/MenuItem/MenuItem';
import { Select } from 'shared/mui/Select/Select';
import { Stack } from 'shared/mui/Stack/Stack';
import { Typography } from 'shared/mui/Typography';
import { Order } from 'types/embedded/order';
import { OrderDispute } from 'types/embedded/orderDispute';

type Props = {
  order: Order | undefined;
  dispute: OrderDispute | undefined;
  onClose: () => void;
} & DialogProps;

export const OrdersShowDisputesEditDialog = ({
  order,
  dispute,
  onClose,
  open,
}: Props): JSX.Element => {
  const { data: orderDisputeStatuses } = useGetList('enum/order_dispute_statuses');
  const form = useForm();

  if (!order || !dispute) {
    return <></>;
  }

  const handleSubmit = () => {
    updateDispute(dispute.id, {
      ...form.getValues(),
      closedAt: form.getValues('closedAt') / 1000,
    }).then(() => {
      queryClient.invalidateQueries({ queryKey: ['order_disputes'] });
      queryClient.invalidateQueries({ queryKey: [Resources.Orders] });
      onClose();
    });
  };

  return (
    <Dialog maxWidth="md" onClose={onClose} open={open}>
      <Card sx={{ width: '640px' }}>
        <form {...form}>
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title="Закрытие диспута"
            titleSize="smallBold"
          />
          <CardContent>
            <Stack gap={3}>
              <Typography>
                Диспут по заказу #{order?.originId || ''} будет закрыт с выбранным статусом.
              </Typography>
              <Controller
                control={form.control}
                name="closedAt"
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <DatePicker
                    error={!!error}
                    helperText={error?.message}
                    label="Дата"
                    maxDate={new Date()}
                    onBlur={onBlur}
                    onChange={onChange}
                    required
                    size="small"
                    value={value || null}
                  />
                )}
                rules={{
                  required: 'Значение не должно быть пустым',
                }}
              />
              <Controller
                control={form.control}
                name="disputeStatus"
                render={({ field, fieldState }) => (
                  <FormControl error={!!fieldState.error} fullWidth>
                    <InputLabel
                      required
                      shrink
                      sx={{
                        backgroundColor: '#fff',
                        padding: '0 5px',
                      }}
                    >
                      Статус
                    </InputLabel>
                    <Select
                      fullWidth
                      label="Статус"
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      size="small"
                      value={field.value}
                      variant="outlined"
                    >
                      {orderDisputeStatuses
                        ?.filter((status) => status.id !== OrderDisputeStatuses.Received)
                        .map((status) => {
                          return (
                            <MenuItem key={status.id} value={status.value}>
                              {status.description}
                            </MenuItem>
                          );
                        })}
                    </Select>
                    {!!fieldState.error && (
                      <FormHelperText>{fieldState.error?.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
                rules={{
                  required: 'Значение не должно быть пустым',
                }}
              />
              <Controller
                control={form.control}
                name="closeReason"
                render={({ field, fieldState }) => (
                  <TextField
                    error={!!fieldState.error}
                    fullWidth
                    helperText={fieldState.error?.message}
                    label="Причина"
                    multiline
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                    required
                    rows={1}
                    size="small"
                    value={field.value}
                    variant="outlined"
                  />
                )}
                rules={{
                  required: 'Значение не должно быть пустым',
                  maxLength: {
                    value: 255,
                    message: 'Значение слишком длинное. Должно быть равно 255 символам или меньше.',
                  },
                }}
              />
            </Stack>
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <Button key="cancel-button" label="Отменить" onClick={onClose} variant="outlined" />,
              <Button
                key="confirm-button"
                label="Подтвердить"
                onClick={form.handleSubmit(handleSubmit)}
                variant="contained"
              />,
            ]}
          ></CardActionsButtons>
        </form>
      </Card>
    </Dialog>
  );
};
